// src/components/dlog/DlogTable.tsx
import React, { useState, useEffect } from 'react';
import SortableTable, { ColumnDefinition } from '../common/SortableTable';
import { DlogItem } from '../../models/Dlog';
import './Dlog.css';

interface DlogTableProps {
  dlogItems: DlogItem[];
  selectedLogId: string | null;
  onLogSelect: (logItem: DlogItem | null) => void;
  onLogRename: (id: string, newName: string) => void;
}

const DlogTable: React.FC<DlogTableProps> = ({
  dlogItems,
  selectedLogId,
  onLogSelect,
  onLogRename,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>('');

  // Reset editing state if selected log changes externally
  useEffect(() => {
    setEditingId(null);
  }, [selectedLogId]);

  const handleNameClick = (item: DlogItem) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditName(event.target.value);
  };

  const handleNameBlur = (id: string) => {
    if (editName.trim() && editName.length <= 40) {
      onLogRename(id, editName.trim());
    }
    setEditingId(null); // Exit edit mode on blur
  };

  const handleNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, id: string) => {
    if (event.key === 'Enter') {
       if (editName.trim() && editName.length <= 40) {
         onLogRename(id, editName.trim());
       }
       setEditingId(null); // Exit edit mode on Enter
       event.preventDefault(); // Prevent form submission if wrapped in form
    } else if (event.key === 'Escape') {
        setEditingId(null); // Exit edit mode on Escape
    }
  };

  const columns: ColumnDefinition<DlogItem>[] = [
    // { key: 'id', label: 'ID', sortable: true, render: (item: DlogItem) => item.id.substring(0, 8) }, // Shorten ID display
    { key: 'index', label: 'ID', sortable: true, render: (item: DlogItem) => item.index}, // Shorten ID display
    {
      key: 'name',
      label: 'Name (Click to Edit)',
      sortable: true,
      render: (item: DlogItem) =>
        editingId === item.id ? (
          <input
            type="text"
            value={editName}
            onChange={handleNameChange}
            onBlur={() => handleNameBlur(item.id)}
            onKeyDown={(e) => handleNameKeyDown(e, item.id)}
            maxLength={40}
            autoFocus
            className="dlog-name-edit"
          />
        ) : (
          <span onClick={() => handleNameClick(item)} className="dlog-name-display">
            {item.name}
          </span>
        ),
    },
    { key: 'contentType', label: 'Content', sortable: true },
    { key: 'dlogType', label: 'Inferred Type', sortable: true },
    { key: 'sizeBytes', label: 'Size (Bytes)', sortable: true, align: 'right', render: (item: DlogItem) => item.sizeBytes.toLocaleString() },
    { key: 'lineCount', label: 'Lines', sortable: true, align: 'right', render: (item: DlogItem) => item.lineCount.toLocaleString() },
    { key: 'loadedTime', label: 'Loaded At', sortable: true, align: 'right', render: (item: DlogItem) => item.loadedTime.toLocaleString() },
  ];

  const handleRowClick = (item: DlogItem) => {
    onLogSelect(item);
    setEditingId(null); // Ensure clicking row exits edit mode
  };

  return (
    <div className="dlog-table-container">
      <SortableTable<DlogItem>
        data={dlogItems}
        columns={columns}
        onRowClick={handleRowClick}
        defaultSortConfig={{ key: 'loadedTime', direction: 'descending' }}
        selectedRowId={selectedLogId} // Pass selected ID for highlighting
        rowIdKey="id" // Specify the key for identifying rows
      />
    </div>
  );
};

export default DlogTable;
